// Task lifecycle status — the ONE place the UI decides "is this work closed?", and
// what a closed row's deadline cell says.
//
// The rule mirrors the DB's task_is_open() (mig 060, which named this exact class
// server-side): done / cancelled / superseded are TERMINAL; everything else is live
// work. Client-side it had been re-typed inline at a dozen call sites — most of them
// as `!['done','cancelled']`, silently omitting 'superseded' — and the list's
// DeadlineClock / the inbox urgency badge checked nothing at all, so a case closed
// weeks ago still painted a red RUNNING statutory countdown ("⚖ Legal · overdue 4d").
// One predicate now backs every countdown / overdue renderer (the TaskQueueStrip
// dot-ladder precedent, generalized).

import { parseISO, isValid, format } from 'date-fns';

export const TERMINAL_TASK_STATUSES = ['done', 'cancelled', 'superseded'] as const;

export function isTerminalTaskStatus(status: string | null | undefined): boolean {
  return !!status && (TERMINAL_TASK_STATUSES as readonly string[]).includes(status);
}

// ══ THE ONE DEADLINE RULE (2026-08-13) ══════════════════════════════════════════
//
// Stated ONCE, in prose. The server states the SAME sentence in SQL —
// backend/src/services/tasks/deadlineClock.ts. Change one, change both: the drift
// between them WAS the bug.
//
// TWO questions, deliberately answered by TWO values — collapsing them into one
// was the follow-on HIGH (see "why sla_deadline is not an overdue clock" below):
//
//   1. WHICH DEADLINE DO WE SHOW?  → bindingDeadline(): the first non-null of
//        hipaa_deadline → statutory_deadline_at → due_date → sla_deadline
//      STATUTORY (⚖ / "Legal ·") when it came from either of the first two,
//      OPERATIONAL otherwise.
//
//   2. IS THIS TASK OVERDUE?       → isTaskOverdue(): the OVERDUE CLOCK is the SAME
//      precedence MINUS sla_deadline —
//        hipaa_deadline → statutory_deadline_at → due_date
//      and the task is OVERDUE when that clock is in the PAST, the task is not
//      TERMINAL, and either the clock is STATUTORY or the status is not 'waiting'
//      (invariant #3 — an operational clock pauses while waiting; statutory ones don't).
//
//   INVARIANT: a surface must NEVER render a FUTURE date in overdue styling. It holds
//   structurally: sla_deadline is LAST in both lists, so whenever an overdue clock
//   exists it IS the binding (displayed) one — overdue ⇒ the date on screen is past.
//
//   "In the past" is read at the resolution the column carries: hipaa_deadline and
//   due_date are DATEs — the LAST day to comply — so they turn past only once that
//   calendar day is OVER **in the practice's own timezone** (setDeadlineTimeZone);
//   statutory_deadline_at and sla_deadline are instants.
//
// WHY sla_deadline IS NOT AN OVERDUE CLOCK (2026-08-13): module_templates.default_sla_hours
// defaults to 48 and resolveTaskDefaults falls back to it, so effectively EVERY rules- and
// fax-spawned task carries sla_deadline = created + 48h while its due_date (rules-engine
// offsets are negative, anchored on appointment_date) is often WEEKS out. With sla_deadline
// ahead of due_date in ONE combined chain, a task due in three weeks went red 48h after
// creation — pill, row tint, quick filter, header count, digests and the Due cell painting
// that FUTURE due date in overdue red. The SLA still surfaces, through the EXISTING and
// correctly-labelled `sla_breached` signal (taskService) — never as "Overdue".
//
// Why hipaa_deadline LEADS: it is the corrected, state-overlay-aware,
// extension-aware §164.524 clock (recordsReleaseService.computeHipaaDeadline =
// request_received_date + LEAST(state turnaround, practice config ?? 30); recomputed
// on a received-date correction; moved by a granted §164.524(b)(2) extension), and it
// is what the case panel and the practice's own on-time compliance report already
// score against. statutory_deadline_at is a BIRTH-TIME SNAPSHOT (fax received_at + a
// FLAT module_templates.statutory_deadline_days, no state overlay, never recomputed)
// and survives only as the fallback for NON-ROI modules that carry one.
//
// The bug this replaces: the list read `statutory_deadline_at || sla_deadline ||
// due_date` — hipaa_deadline was absent from the chain AND from the frontend Task
// type — so a reclassified-into-ROI case (statutory_deadline_at NULL) fell through to
// a 24h triage SLA and painted red forever, while its own case panel showed
// "HIPAA: 21d remaining". Same case, two clocks, four independent overdue rules.
//
// NOTE — this is a READ/DISPLAY unification only. Nothing about how hipaa_deadline or
// statutory_deadline_at are WRITTEN changes. In particular the reclassify path that
// NULLs statutory_deadline_at without re-stamping it stops mattering the moment
// hipaa_deadline leads the precedence, so it is deliberately left alone.

export type DeadlineSource = 'hipaa_deadline' | 'statutory_deadline_at' | 'sla_deadline' | 'due_date';

export interface DeadlineClockInput {
  status?: string | null;
  hipaa_deadline?: string | null;
  statutory_deadline_at?: string | null;
  sla_deadline?: string | null;
  due_date?: string | null;
}

export interface BindingDeadline {
  source: DeadlineSource;
  at: Date;
  /** A LEGAL deadline (⚖ / "Legal ·"), not an internal service target. */
  statutory: boolean;
  /** The column is a DATE — the LAST day to comply — not an instant. */
  dateOnly: boolean;
}

// The DISPLAY precedence, in order. `statutory` and `dateOnly` are properties OF THE
// COLUMN, so they can never be re-derived (wrongly) at a call site.
const DEADLINE_PRECEDENCE: ReadonlyArray<Omit<BindingDeadline, 'at'>> = [
  { source: 'hipaa_deadline',        statutory: true,  dateOnly: true  },
  { source: 'statutory_deadline_at', statutory: true,  dateOnly: false },
  { source: 'due_date',              statutory: false, dateOnly: true  },
  { source: 'sla_deadline',          statutory: false, dateOnly: false },
];

// The OVERDUE precedence — the same list MINUS sla_deadline. An auto-populated 48h
// internal target is not an accountability clock; `sla_breached` is its signal.
const OVERDUE_PRECEDENCE: ReadonlyArray<Omit<BindingDeadline, 'at'>> = [
  { source: 'hipaa_deadline',        statutory: true,  dateOnly: true  },
  { source: 'statutory_deadline_at', statutory: true,  dateOnly: false },
  { source: 'due_date',              statutory: false, dateOnly: true  },
];

/**
 * Parse a deadline column value the way THIS module reads it back.
 *
 * The server sends a DATE as UTC midnight ('2026-08-20T00:00:00.000Z') and every DATE
 * reader here (dateColumnDayIndex / formatDeadlineAt) takes its UTC parts. But a value can
 * also arrive as a BARE 'YYYY-MM-DD' — QuickEditPopover's optimistic cache patch writes the
 * native date picker's raw draft into ['tasks-v2'] / ['task-children'] — and date-fns
 * parseISO of a date-only string returns LOCAL midnight, which carries the machine's offset.
 * For a viewer AHEAD of UTC — the offshore (IST) coordinators, local midnight being 18:30 UTC
 * the PREVIOUS day — its UTC parts name the day before, so from the click until the refetch
 * landed the row they had just saved read one day early, and one day early on a DATE clock is
 * also one day LATE on the overdue rule. (West of UTC the offset lands inside the same UTC day,
 * which is exactly why a UTC or US machine cannot observe this.)
 *
 * A bare day means the day itself, so it is normalised to UTC midnight HERE — at the one entry
 * point every caller already goes through — rather than at whichever call site produced the shape.
 */
export function parseDeadlineValue(iso: string): Date {
  return parseISO(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00.000Z` : iso);
}

function resolveClock(
  task: DeadlineClockInput,
  precedence: ReadonlyArray<Omit<BindingDeadline, 'at'>>,
): BindingDeadline | null {
  for (const col of precedence) {
    const iso = task[col.source];
    if (!iso) continue;
    const at = parseDeadlineValue(iso);
    if (!isValid(at)) continue;
    return { ...col, at };
  }
  return null;
}

/** The ONE binding deadline of a task — value, legal-ness and resolution together. */
export function bindingDeadline(task: DeadlineClockInput): BindingDeadline | null {
  return resolveClock(task, DEADLINE_PRECEDENCE);
}

/** The clock the OVERDUE test runs on: the binding precedence minus sla_deadline. */
export function overdueClock(task: DeadlineClockInput): BindingDeadline | null {
  return resolveClock(task, OVERDUE_PRECEDENCE);
}

/** Is the binding deadline a LEGAL one? Drives the ⚖ / "Legal ·" prefix (D2). */
export function isStatutoryDeadline(task: DeadlineClockInput): boolean {
  return bindingDeadline(task)?.statutory ?? false;
}

// ── A DATE is a CALENDAR DAY, not an instant ────────────────────────────────────
//
// hipaa_deadline and due_date are bare Postgres DATEs meaning "the LAST day to comply".
// node-pg hands them back as UTC midnight, so they serialize as '2026-08-20T00:00:00Z'.
// Reading them as instants flipped them at UTC midnight — a Pacific practice saw
// "⚖ Legal · overdue 1d" at 17:00 PDT ON the last day to comply. The governing calendar
// is the PRACTICE's, so the day basis is the practice's IANA zone (practices.timezone,
// mig 206), mirrored server-side in deadlineClock.ts's localTodaySql.
//
// WHOSE CALENDAR (founder decision, 2026-08-13): `practices.timezone` when the practice
// has set one, ELSE the platform default below — on BOTH halves, identically. The viewer's
// own zone is NEVER a fallback: an offshore (IST) coordinator and a US supervisor looking
// at the SAME case must read the SAME number, so the day basis belongs to the case, not to
// the browser. The server resolves the same default (deadlineClock.DEFAULT_PRACTICE_TIMEZONE,
// overridable by the DEFAULT_PRACTICE_TIMEZONE env var) and SHIPS it on /auth/me as
// `deadline_timezone_default`, so one env change moves both halves and they cannot diverge.
// The client-side resolution (selected practice → own practice → server default) lives in
// store/index.ts `resolveDeadlineTimeZone`; this module only holds the resolved value.
// Known, accepted trade-off: until a practice configures its zone, a US practice's deadline
// flips at UTC midnight (late afternoon local).

/** The platform default day basis — the SAME fallback the server resolves to. */
export const DEFAULT_DEADLINE_TIME_ZONE = 'UTC';

/** The zone whose calendar day a DATE deadline is read in. Never the viewer's own. */
let deadlineTimeZone: string = DEFAULT_DEADLINE_TIME_ZONE;

/**
 * Set the day basis (AppShell and DoctorReviewPage — which mounts OUTSIDE the shell — both
 * from store/index.ts `resolveDeadlineTimeZone`). Null/empty/unusable resets to the platform
 * default, so a bad stored value can never break a render AND can never silently hand the
 * calendar back to the viewer's own zone.
 */
export function setDeadlineTimeZone(tz: string | null | undefined): void {
  deadlineTimeZone = tz || DEFAULT_DEADLINE_TIME_ZONE;
}

/** Days-since-epoch of the calendar day `ms` falls on, in the deadline zone. */
function todayDayIndex(ms: number): number {
  try {
    const ymd = new Date(ms).toLocaleDateString('en-CA', { timeZone: deadlineTimeZone });
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86_400_000;
  } catch {
    /* unusable IANA name → the platform default, exactly as the server's zoneSql CASE does */
  }
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86_400_000;
}

/** Days-since-epoch of the calendar day a DATE column carries (its UTC-midnight parts). */
function dateColumnDayIndex(at: Date): number {
  return Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()) / 86_400_000;
}

/**
 * Whole days until `at`. Negative = days past.
 * `dateOnly` (a property of the COLUMN — pass `BindingDeadline.dateOnly`) switches from
 * instant arithmetic to CALENDAR-DAY arithmetic in the practice's zone.
 */
export function deadlineDaysLeft(at: Date, now: number = Date.now(), dateOnly: boolean = false): number {
  if (dateOnly) return dateColumnDayIndex(at) - todayDayIndex(now);
  return Math.ceil((at.getTime() - now) / 86_400_000);
}

/**
 * Render a deadline at the resolution its column carries — THE formatter for every
 * displayed binding deadline. A DATE column is printed from its UTC parts (the calendar
 * day it actually names); feeding that UTC-midnight instant to a local formatter printed
 * "Aug 19" for an Aug 20 deadline everywhere west of UTC. An instant column is printed in
 * the viewer's own time, which is what it means.
 */
export function formatDeadlineAt(at: Date, dateOnly: boolean, pattern: string = 'MMM d, yyyy'): string {
  const d = dateOnly ? new Date(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()) : at;
  return format(d, pattern);
}

/** `formatDeadlineAt` over a resolved clock — the call every surface should make. */
export function formatDeadline(d: BindingDeadline, pattern?: string): string {
  return formatDeadlineAt(d.at, d.dateOnly, pattern);
}

/**
 * Is this clock past, at the resolution its column carries? Exported because a passed clock
 * is not always an OVERDUE one (sla_deadline), and the surfaces that say so out loud must
 * not re-derive "past" from the rounded day count: an SLA that expired an hour ago rounds to
 * 0 days, which read as "today" / "Due in 0d" — a passed internal target described as ahead.
 */
export function isDeadlinePast(d: BindingDeadline, now: number = Date.now()): boolean {
  return d.dateOnly ? deadlineDaysLeft(d.at, now, true) < 0 : d.at.getTime() < now;
}

/** THE word for a passed clock that is NOT a breach — one string, three renderers. */
export const SLA_PAST_LABEL = 'SLA past';

/**
 * The SLA-BREACH signal — the SEPARATE, correctly-labelled reason an auto 48h internal
 * target surfaces through, since it is deliberately absent from the overdue precedence.
 * States the server's Supervisor-Exceptions arm ONCE, client-side:
 *   taskService: `t.sla_deadline < NOW() AND task_is_actionable(t.status)`
 *   mig 060:     task_is_actionable = NOT IN ('done','cancelled','superseded','waiting')
 * — so a waiting task is excluded here too (invariant #3: its operational clock is held,
 * and taskService.setStatus literally pushes sla_deadline out by the parked time on resume).
 * A row admitted to the exceptions board on THIS arm alone must render THIS reason, or it
 * arrives with no reason chip at all.
 */
export function isSlaBreached(task: DeadlineClockInput, now: number = Date.now()): boolean {
  if (isTerminalTaskStatus(task.status) || task.status === 'waiting') return false;
  if (!task.sla_deadline) return false;
  const at = parseISO(task.sla_deadline);
  return isValid(at) && at.getTime() < now;
}

/**
 * THE overdue rule — the pill, the row tint, the count, the quick filter and the
 * Due cell all derive from THIS and nothing else. Mirrored in SQL by
 * backend/src/services/tasks/deadlineClock.ts `taskOverdueSql`.
 *
 * The clock is the OVERDUE precedence (no sla_deadline), NOT the display one.
 */
export function isTaskOverdue(task: DeadlineClockInput, now: number = Date.now()): boolean {
  if (isTerminalTaskStatus(task.status)) return false;
  const d = overdueClock(task);
  if (!d) return false;
  // invariant #3 — an operational clock pauses while waiting; a statutory one never does.
  if (!d.statutory && task.status === 'waiting') return false;
  // A DATE is the LAST day to comply: past only once that calendar day is over in the
  // practice's zone — exactly the server's `col < (NOW() AT TIME ZONE <zone>)::date`.
  return isDeadlinePast(d, now);
}

// ── The Due-cell clock decision (TaskListView.DeadlineClock renders it) ──────────
// Kept pure and out of the TSX so the rule above and the M5 threshold are unit-testable.
export type DeadlineClockState =
  | { kind: 'none' }
  | { kind: 'closed'; statutory: boolean }
  | {
      kind: 'running';
      statutory: boolean;
      days: number;
      label: string;
      danger: boolean;
      /** THE overdue rule (isTaskOverdue) — never re-derive it from `days`. */
      overdue: boolean;
      /** An OPERATIONAL clock held by `waiting` — it is not running, so it is not late. */
      paused: boolean;
    };

export function deadlineClockState(task: DeadlineClockInput, now: number): DeadlineClockState {
  const d = bindingDeadline(task);
  if (!d) return { kind: 'none' };
  // A closed task's clock stopped when it closed — no countdown, no red. The row's
  // Status cell already says done / cancelled / superseded.
  if (isTerminalTaskStatus(task.status)) return { kind: 'closed', statutory: d.statutory };
  const days = deadlineDaysLeft(d.at, now, d.dateOnly);
  const overdue = isTaskOverdue(task, now);
  const paused = !d.statutory && task.status === 'waiting';
  // "Past" is read off the CLOCK, not off the rounded day count: an sla_deadline that
  // expired an hour ago rounds to 0 days and used to read 'today'.
  const past = isDeadlinePast(d, now);
  // The wording follows THE rule, never the raw sign of `days` — a chip that reads
  // "overdue 1d" while the pill and the header count say otherwise is the same
  // display-value-as-overdue-test confusion one layer down. `past && !overdue` is
  // reachable ONLY when sla_deadline is the displayed clock (it is last in the display
  // list and absent from the overdue one), and a passed internal target is exactly what
  // the separately-labelled SLA-breach signal reports.
  const label = paused
    ? 'paused'
    : overdue ? (days < 0 ? `overdue ${Math.abs(days)}d` : 'overdue')
    : past ? SLA_PAST_LABEL
    : days === 0 ? 'today'
    : `${days} day${days === 1 ? '' : 's'} left`;
  // M5 — red at ≤2 (not ≤3) so the same statutory clock turns red on the SAME day in the
  // list and in the drawer/HipaaDeadlineBadge (which already redden at ≤2). Past-deadline
  // red comes from THE rule, so a paused (or merely passed) SLA clock is never red.
  return { kind: 'running', statutory: d.statutory, days, label, danger: overdue || (!paused && !past && days >= 0 && days <= 2), overdue, paused };
}
